import React, { useEffect, useState } from "react";

import { getDispensaryDeals } from "@/api/requests/getDispensaryDeals";
import { Action, Category } from "@/constants/events";
import { Deal } from "@/custom-types/Deals";
import { Dispensary } from "@/custom-types/Dispensary";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getUserId, getUserPrefersMedical } from "@/redux/selectors/user";
import isDispensaryDualLicense from "@/utils/dispensary/isDispensaryDualLicense";
import formatDeals from "@/utils/formatDeals";
import { getDealKindLabel } from "@/utils/getDealKindLabel";

import DealCard, { DealCardPlaceholder } from "@/components/botanic//DealCard";
import Button from "@/components/botanic/Button";
import MenuTypeSelection from "@/components/Shared/MenuTypeSelection";

import NoDealsOrPromos from "../NoDealsOrPromos";

const DEALS_PER_CLICK = 21;

const DealSectionDealCard: React.FC<{ deal: Deal; dispensaryId: number }> = ({
  deal,
  dispensaryId,
}) => {
  const { publishEvent } = useEventTracker();
  const userId = useSelector(getUserId);

  const sharedEventProps = {
    category: Category.dealCards,
    dealId: deal.id,
    dispensaryId: dispensaryId,
    label: getDealKindLabel(deal),
    userId,
  };

  return (
    <div className="col md:col-1/2 lg:col-1/3 mb-sm">
      <DealCard
        className="rounded h-full overflow-hidden shadow-low"
        data-testid="deal-section__deal-card"
        deal={{ ...deal, dealUrl: deal.link }}
        showMenuType
        onClick={() =>
          publishEvent({
            action: Action.click,
            ...sharedEventProps,
          })
        }
        onLoad={() =>
          publishEvent({
            action: Action.impression,
            nonInteractionEvent: true,
            ...sharedEventProps,
          })
        }
      />
    </div>
  );
};

const DealsSection: React.FC<{
  initialDeals: Deal[];
  dispensary: Dispensary;
  totalDealsCount: number;
}> = ({ initialDeals, dispensary, totalDealsCount }) => {
  const {
    todaysDeals,
    todaysMedDeals,
    todaysRecDeals,
    upcomingDeals,
    upcomingMedDeals,
    upcomingRecDeals,
  } = formatDeals(initialDeals);

  const countryCode = useDomainCountryCode();

  const userPrefersMedical = useSelector(getUserPrefersMedical);

  const isDualLicenseDispensary = isDispensaryDualLicense(dispensary.tags);

  const selectTodaysDeals = isDualLicenseDispensary
    ? userPrefersMedical
      ? todaysMedDeals
      : todaysRecDeals
    : todaysDeals;
  const selectUpcomingDeals = isDualLicenseDispensary
    ? userPrefersMedical
      ? upcomingMedDeals
      : upcomingRecDeals
    : upcomingDeals;

  const hasDeals = !!(selectTodaysDeals.length || selectUpcomingDeals.length);
  const [deals, setDeals] = useState<Deal[]>(initialDeals || []);
  const [todaysDisplayedDeals, setTodaysDisplayedDeals] = useState<Deal[]>(
    selectTodaysDeals || [],
  );
  const [visibleDealsCount, setVisibleDealsCount] = useState(DEALS_PER_CLICK);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [numberOfDealsCalls, setNumberOfDealsCalls] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const { todaysDeals, todaysMedDeals, todaysRecDeals } = formatDeals(deals);
    const selectTodaysDeals = isDualLicenseDispensary
      ? userPrefersMedical
        ? todaysMedDeals
        : todaysRecDeals
      : todaysDeals;

    setTodaysDisplayedDeals(selectTodaysDeals);
    setShowLoadMore(totalDealsCount > visibleDealsCount);
  }, [deals, isDualLicenseDispensary, userPrefersMedical, visibleDealsCount]);

  const handleLoadMoreClick = async () => {
    setIsLoading(true);
    if (!showLoadMore) {
      return;
    }
    getDispensaryDeals(
      dispensary.slug,
      21,
      DEALS_PER_CLICK * numberOfDealsCalls,
    ).then((response) => {
      setDeals(deals.concat(response.data));
    });
    setNumberOfDealsCalls(numberOfDealsCalls + 1);
    setVisibleDealsCount(visibleDealsCount + DEALS_PER_CLICK);
    setIsLoading(false);
  };

  return (
    <div data-testid="deals__today-section" className="mb-xxl">
      <div className="flex items-start justify-between w-full mb-lg">
        <div>
          <h2>
            {countryCode === "CA"
              ? "Current licensed dispensary deals near you"
              : "Current dispensary deals near you"}
          </h2>
          {hasDeals && (
            <p className="mb-none">
              Order these limited-time deals for pickup (or delivery, where it's
              legal)!
            </p>
          )}
        </div>
        <MenuTypeSelection
          dispensaryId={dispensary.id}
          className="ml-md text-right"
          isDualLicense={isDualLicenseDispensary}
          showSelectInput={true}
        />
      </div>

      {!hasDeals && <NoDealsOrPromos type="deals" />}

      {todaysDisplayedDeals.length > 0 && (
        <>
          <h3 className="text-md mb-xs">Available today</h3>
          <div className="row">
            {todaysDisplayedDeals.map((deal) => (
              <DealSectionDealCard
                key={deal.id}
                deal={deal}
                dispensaryId={dispensary.id}
              />
            ))}
            {(todaysDisplayedDeals === undefined || isLoading) &&
              Array(
                totalDealsCount - visibleDealsCount >= DEALS_PER_CLICK
                  ? DEALS_PER_CLICK
                  : totalDealsCount - visibleDealsCount,
              )
                .fill(null)
                .map((_, i) => (
                  <div
                    className="col md:col-1/3 mb-md"
                    key={`deals-section-placeholder-${i}`}
                  >
                    <DealCardPlaceholder />
                  </div>
                ))}
          </div>
          {showLoadMore && (
            <Button
              data-testid="load-more-deals"
              variant="secondary"
              onClick={handleLoadMoreClick}
              className="mt-xl"
            >
              load more deals
            </Button>
          )}
        </>
      )}

      {selectUpcomingDeals.length > 0 && (
        <>
          <h3 className="text-green mt-xxl mb-xs">Upcoming deals</h3>
          <div className="row">
            {selectUpcomingDeals.map((deal) => (
              <DealSectionDealCard
                key={deal.id}
                deal={deal}
                dispensaryId={dispensary.id}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DealsSection;
